import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Create axios instance with HTTP-only cookie support
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor (cookies are sent automatically with withCredentials)
api.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Queue for failed requests during token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = originalRequest.url || '';

    // Check if this is an auth-related request (login, logout, refresh, etc)
    const isAuthRequest = requestUrl.includes('/auth/login') || 
                         requestUrl.includes('/auth/logout') || 
                         requestUrl.includes('/auth/refresh') ||
                         requestUrl.includes('/auth/change-password') ||
                         requestUrl.includes('/auth/verify-password');

    // Prevent infinite loops if the refresh token request itself fails
    if (requestUrl.includes('/auth/refresh')) {
      console.error('Token refresh endpoint failed:', error.response?.data?.message || error.message);
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    // Never attempt token refresh for any auth-related request
    if (isAuthRequest && error.response?.status === 401) {
      console.warn('Auth request failed with 401:', requestUrl);
      return Promise.reject(error);
    }

    // Check for specific inactivity timeout error
    if (error.response?.data?.code === 'INACTIVITY_TIMEOUT') {
      console.warn('Inactivity timeout detected, redirecting to login...');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    // If error is 401 for non-auth requests, attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      console.log('Access token expired, attempting refresh...');
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Refresh token endpoint will set new token in HTTP-only cookie
        await api.post('/api/admin/auth/refresh');
        console.log('Token refresh successful');

        processQueue(null, true);
        isRefreshing = false;

        // Token is now in cookie, retry original request
        return api(originalRequest);
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        processQueue(refreshError, null);
        isRefreshing = false;

        // Refresh failed, logout user by redirecting to login
        if (window.location.pathname !== '/login') {
          console.error('Refresh failed for URL:', originalRequest.url, '- Redirecting to login');
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/api/admin/auth/login', credentials),
  logout: () => api.post('/api/admin/auth/logout'),
  getProfile: () => api.get('/api/admin/auth/me'),
  changePassword: (data) => api.put('/api/admin/auth/change-password', data),
  refreshToken: (refreshToken) => api.post('/api/admin/auth/refresh', { refreshToken }),
  verifyPasswordForInactivity: (password) => api.post('/api/admin/auth/verify-password', { password }),
  setup2FA: () => api.post('/api/admin/auth/2fa/setup'),
  enable2FA: (token) => api.post('/api/admin/auth/2fa/enable', { token }),
  disable2FA: (password) => api.post('/api/admin/auth/2fa/disable', { password }),
  verify2FA: (data) => api.post('/api/admin/auth/2fa/verify', data),
};

// User Management API
export const userAPI = {
  createUser: (userData) => api.post('/api/admin/users', userData),
  getAllUsers: (params) => api.get('/api/admin/users', { params }),
  getUserById: (id) => api.get(`/api/admin/users/${id}`),
  updateUser: (id, userData) => api.put(`/api/admin/users/${id}`, userData),
  deleteUser: (id) => api.delete(`/api/admin/users/${id}`),
  toggleUserStatus: (id) => api.patch(`/api/admin/users/${id}/status`),
  getUserScreenUrl: (id) => api.get(`/api/admin/users/${id}/url`),
  getUserScreens: (id) => api.get(`/api/admin/users/${id}/screens`),
  getUserContent: (id, params) => api.get(`/api/admin/users/${id}/content`, { params }),
};

// Screen Management API (to be implemented in backend)
export const screenAPI = {
  createScreen: (screenData) => api.post('/api/admin/screens', screenData),
  getAllScreens: (params) => api.get('/api/admin/screens', { params }),
  getScreenById: (id) => api.get(`/api/admin/screens/${id}`),
  updateScreen: (id, screenData) => api.put(`/api/admin/screens/${id}`, screenData),
  deleteScreen: (id) => api.delete(`/api/admin/screens/${id}`),
  assignScreen: (id, userId) => api.post(`/api/admin/screens/${id}/assign`, { userId }),
  unassignScreen: (id) => api.post(`/api/admin/screens/${id}/unassign`),
  bulkAssignScreens: (data) => api.post('/api/admin/screens/bulk-assign', data),
  getScreenContent: (id, params) => api.get(`/api/admin/screens/${id}/content`, { params }),
  changeScreenStatus: (id, status) => api.patch(`/api/admin/screens/${id}/status`, { status }),
  toggleScreenStatus: (id) => api.patch(`/api/admin/screens/${id}/status`),
};

// Content Management API (to be implemented in backend)
export const contentAPI = {
  getAllContent: (params) => api.get('/api/admin/content', { params }),
  getPendingContent: (params) => api.get('/api/admin/content/pending', { params }),
  getApprovedContent: (params) => api.get('/api/admin/content/approved', { params }),
  getRejectedContent: (params) => api.get('/api/admin/content/rejected', { params }),
  getContentById: (id) => api.get(`/api/admin/content/${id}`),
  approveContent: (id, data) => api.put(`/api/admin/content/${id}/approve`, data),
  rejectContent: (id, data) => api.put(`/api/admin/content/${id}/reject`, data),
  bulkApprove: (ids) => api.post('/api/admin/content/bulk-approve', { ids }),
  bulkReject: (ids, reason) => api.post('/api/admin/content/bulk-reject', { ids, reason }),
  deleteContent: (id) => api.delete(`/api/admin/content/${id}`),
  revokeApproval: (id) => api.patch(`/api/admin/content/${id}/revoke`),
};

// Activity Logs API (to be implemented in backend)
export const activityAPI = {
  getActivityLogs: (params) => api.get('/api/admin/activity-logs', { params }),
  exportLogs: (params) => api.get('/api/admin/activity-logs/export', { params, responseType: 'blob' }),
};

// Dashboard/Stats API (to be implemented in backend)
export const dashboardAPI = {
  getStats: () => api.get('/api/admin/dashboard/stats'),
  getRecentActivity: (limit) => api.get('/api/admin/dashboard/recent-activity', { params: { limit } }),
};

// Location Management API
export const locationAPI = {
  getAllLocations: (params) => api.get('/api/admin/locations', { params }),
  getLocationById: (id) => api.get(`/api/admin/locations/${id}`),
  createLocation: (data) => api.post('/api/admin/locations', data),
  updateLocation: (id, data) => api.put(`/api/admin/locations/${id}`, data),
  deleteLocation: (id, force = false) => api.delete(`/api/admin/locations/${id}${force ? '?force=true' : ''}`),
  getLocationStats: () => api.get('/api/admin/locations/stats/overview'),
  getLocationsDropdown: () => api.get('/api/admin/locations/dropdown'),
};

// Campaign Management API
export const campaignAPI = {
  getAllCampaigns: (params) => api.get('/api/admin/campaigns', { params }),
  getCampaignById: (id) => api.get(`/api/admin/campaigns/${id}`),
  createCampaign: (data) => api.post('/api/admin/campaigns', data),
  updateCampaign: (id, data) => api.put(`/api/admin/campaigns/${id}`, data),
  deleteCampaign: (id) => api.delete(`/api/admin/campaigns/${id}`),
  uploadCreative: (id, formData) => api.post(`/api/admin/campaigns/${id}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  assignSlots: (id, data) => api.post(`/api/admin/campaigns/${id}/assign-slots`, data),
  submitForApproval: (id) => api.post(`/api/admin/campaigns/${id}/submit`),
  approveCampaign: (id, data) => api.post(`/api/admin/campaigns/${id}/approve`, data),
  rejectCampaign: (id, data) => api.post(`/api/admin/campaigns/${id}/reject`, data),
  pauseCampaign: (id) => api.post(`/api/admin/campaigns/${id}/pause`),
  resumeCampaign: (id) => api.post(`/api/admin/campaigns/${id}/resume`),
  updateManualImpressions: (id, data) => api.post(`/api/admin/campaigns/${id}/manual-impressions`, data),
  getCampaignStats: () => api.get('/api/admin/campaigns/stats/overview'),
  checkSlotAvailability: (data) => api.post('/api/admin/campaigns/check-availability', data),
};

// Report Generation API
export const reportAPI = {
  downloadSlotAvailability: (params) => api.get('/api/admin/reports/slot-availability', {
    params,
    responseType: 'blob'
  }),
  downloadCampaignPerformance: (params) => api.get('/api/admin/reports/campaign-performance', {
    params,
    responseType: 'blob'
  }),
  downloadImpressions: (params) => api.get('/api/admin/reports/impressions', {
    params,
    responseType: 'blob'
  }),
  downloadLocationSummary: (params) => api.get('/api/admin/reports/locations', {
    params,
    responseType: 'blob'
  }),
};

// Impression Tracking API
export const impressionAPI = {
  getCampaignImpressions: (campaignId, params) => api.get(`/api/admin/impressions/campaign/${campaignId}`, { params }),
  getImpressionStats: (params) => api.get('/api/admin/impressions/stats', { params }),
};

// Enhanced Screen API with new endpoints
export const enhancedScreenAPI = {
  ...screenAPI,
  getScreenSlots: (id) => api.get(`/api/admin/screens/${id}/slots`),
  controlScreenPower: (id, data) => api.post(`/api/admin/screens/${id}/power`, data),
  getScreensByLocation: (locationId) => api.get(`/api/admin/screens/location/${locationId}`),
};

export default api;